import { useEffect, useMemo, useState } from 'react'

const EMPTY_ADD_FORM = {
	contact_name: '',
	email: '',
	contact_type: 'parent',
	is_primary: false,
}

const isValidEmail = email => {
	const value = String(email || '').trim()
	if (!value || !value.includes('@')) return false
	const domain = value.split('@').pop()
	return Boolean(domain && domain.includes('.'))
}

export const isContactFormValid = form => {
	const name = String(form?.contact_name || '').trim()
	const email = String(form?.email || '').trim()
	const contactType = String(form?.contact_type || '').trim()
	return (
		Boolean(name) &&
		isValidEmail(email) &&
		['parent', 'guardian', 'emergency_contact'].includes(contactType)
	)
}

export const firstName = name => {
	const value = String(name || '').trim()
	if (!value) return 'Contact'
	return value.split(/\s+/)[0] || value
}

export const useContactModal = ({
	contactDraft,
	updateContactField,
	updateAddFormField,
	submitContact,
	deleteContact,
	contactSaving,
	openAddContact,
}) => {
	const isAddMode = contactDraft.mode === 'add'
	const contacts = useMemo(
		() => contactDraft.contacts || [],
		[contactDraft.contacts],
	)

	const [selectedId, setSelectedId] = useState(() => {
		if (isAddMode) return null
		const initialId = contactDraft.initialContactId
		if (initialId && contacts.some(contact => contact.id === initialId)) {
			return initialId
		}
		const primary = contacts.find(contact => contact.is_primary)
		return primary?.id || contacts[0]?.id || null
	})

	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)

	useEffect(() => {
		if (isAddMode) {
			setSelectedId(null)
			return
		}
		const stillExists = contacts.some(contact => contact.id === selectedId)
		if (!stillExists) {
			const primary = contacts.find(contact => contact.is_primary)
			setSelectedId(primary?.id || contacts[0]?.id || null)
		}
	}, [contacts, isAddMode, selectedId])

	const addForm = isAddMode ? contactDraft.form || EMPTY_ADD_FORM : null

	const activeContact =
		!isAddMode && selectedId
			? contacts.find(contact => contact.id === selectedId)
			: null

	const onFieldChange = (field, value) => {
		if (isAddMode) {
			updateAddFormField(field, value)
			return
		}
		if (!selectedId) return
		updateContactField(selectedId, field, value)
	}

	const canSave = useMemo(() => {
		if (contactSaving) return false
		if (isAddMode) return isContactFormValid(addForm)
		if (!activeContact) return false
		return contacts.every(isContactFormValid)
	}, [activeContact, addForm, contactSaving, contacts, isAddMode])

	const handleSubmit = event => {
		event.preventDefault()
		submitContact(event)
	}

	const switchToAddContact = () => {
		if (contactSaving || !openAddContact) return
		openAddContact({
			studentId: contactDraft.studentId,
			studentName: contactDraft.studentName,
			grade: contactDraft.grade,
			avatarKey: contactDraft.avatarKey,
		})
	}

	const requestDelete = () => {
		if (!activeContact || contactSaving || !deleteContact) return
		setDeleteConfirmOpen(true)
	}

	const cancelDelete = () => {
		if (contactSaving) return
		setDeleteConfirmOpen(false)
	}

	const confirmDelete = async () => {
		if (!activeContact || contactSaving || !deleteContact) return
		await deleteContact(activeContact.id)
		setDeleteConfirmOpen(false)
	}

	const displayName = isAddMode
		? String(addForm?.contact_name || '').trim() || 'New contact'
		: activeContact?.contact_name || 'Contact'

	return {
		isAddMode,
		contacts,
		selectedId,
		setSelectedId,
		addForm,
		activeContact,
		onFieldChange,
		canSave,
		handleSubmit,
		switchToAddContact,
		requestDelete,
		cancelDelete,
		confirmDelete,
		deleteConfirmOpen,
		displayName,
	}
}

export default useContactModal
